/**
 * Jobs, per client — the one rule for every place a biller picks jobs.
 *
 * The owner, 2026-09-25 and again 2026-09-26: jobs must be findable by client
 * when billing. /available-jobs answers finished first, newest first
 * (routes/invoices.js orderClause), so one client's jobs arrive scattered
 * among everyone else's. Bill finished jobs (screens/InvoiceFromJobs.js) and
 * the invoice/estimate editor's "Link jobs" (DocumentEditor.js) both group and
 * filter with these, so the two can never disagree about who a job belongs to.
 */

export const NO_CLIENT = 'No client';
const byName = (a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' });

/**
 * The group a job belongs to: its client_id, or 'none'.
 * A blank client_id is no client too: keyed '' it would sit in no option and
 * outside the 'none' bucket, reachable only under "Any client" (#711 review).
 * InvoiceFromJobs' clientConflict keys a blank id as its own client instead —
 * it can only REFUSE a pick that mixes it with a null one, the safe direction
 * for a bill, so it is left as it is.
 */
export function clientKey(job) {
  return job.client_id == null || job.client_id === '' ? 'none' : String(job.client_id);
}

/**
 * The rows as groups, one per client: A–Z by name, "No client" last. Within a
 * group the server's order stands (finished first, newest first).
 */
export function groupByClient(rows) {
  const groups = new Map();
  for (const j of rows || []) {
    const key = clientKey(j);
    // A client row with a blank name is still ONE client: named by its id,
    // never "No client" — that label belongs to jobs with no client at all
    // (#710 review).
    if (!groups.has(key)) groups.set(key, { key, name: key === 'none' ? NO_CLIENT : (j.client_name || `Client #${key}`), jobs: [] });
    groups.get(key).jobs.push(j);
  }
  return [...groups.values()].sort((a, b) => {
    if ((a.key === 'none') !== (b.key === 'none')) return a.key === 'none' ? 1 : -1;
    return byName(a.name, b.name);
  });
}

/** The client dropdown's options, A–Z — not the order the jobs happened to arrive in. */
export function clientOptions(jobs) {
  const seen = new Map();
  // A blank name reads as the group heading does, `Client #id` — "No client"
  // here would offer a real client under the label for jobs with none (#710).
  for (const j of jobs || []) if (j.client_id && !seen.has(String(j.client_id))) seen.set(String(j.client_id), j.client_name || `Client #${j.client_id}`);
  return [...seen].map(([id, name]) => ({ id, name })).sort((a, b) => byName(a.name, b.name));
}
